import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Switch, Chip, Card, CardContent, Grid, Button,
  TextField, Alert, Tooltip, ToggleButton, ToggleButtonGroup, Stack, IconButton, Divider
} from '@mui/material';
import {
  Tune as ControlsIcon, Lock as LockIcon, CheckCircle as CheckCircleIcon,
  Warning as WarningIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  ViewList as TableIcon, ViewModule as GridIcon, ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import GenericTable from '../../Commun/components/GenericTable';
import { academyStore } from '../services/academyStore';
import { PlanningControl } from '../types/academy';
import { useLanguage } from '../../Commun/contexts/LanguageContext';

const ENTITY_KEY = 'Blobtic_Academy,academy_planning_control';
const ACTION_SUFFIX = '';
const PRIMARY_KEY = 'CTL_IDF';

const dtoToControl = (dto: any): PlanningControl => ({
  id: dto.CTL_IDF || dto.id,
  code: dto.CTL_CODE || dto.code || '',
  lib: dto.CTL_LIB || dto.lib || '',
  is_active: dto.CTL_IS_ACTIVE != null ? Boolean(dto.CTL_IS_ACTIVE) : (dto.is_active ?? true),
  is_blocking: dto.CTL_IS_BLOCKING != null ? Boolean(dto.CTL_IS_BLOCKING) : (dto.is_blocking ?? true),
  description: dto.CTL_DESCRIPTION || dto.description || '',
  user_lib: dto.USER_LIB || dto.user_lib || undefined,
  last_updt: dto.LAST_UPDT
    ? (typeof dto.LAST_UPDT === 'string' ? dto.LAST_UPDT : new Date(dto.LAST_UPDT).toLocaleString('fr-DZ'))
    : (dto.last_updt || undefined),
});

const controlToDto = (c: Partial<PlanningControl>): Record<string, any> => ({
  CTL_IDF: (c as any).CTL_IDF ?? c.id,
  CTL_CODE: c.code || '',
  CTL_LIB: c.lib || '',
  CTL_IS_ACTIVE: c.is_active != null ? (c.is_active ? 1 : 0) : 1,
  CTL_IS_BLOCKING: c.is_blocking != null ? (c.is_blocking ? 1 : 0) : 1,
  CTL_DESCRIPTION: c.description || '',
  ...(c.user_lib ? { USER_LIB: c.user_lib } : {}),
});

const emptyControl = (): Partial<PlanningControl> => ({
  code: 'CTL_CUSTOM_' + Math.floor(100 + Math.random() * 900),
  lib: '',
  is_active: true,
  is_blocking: true,
  description: ''
});

export const PlanningControlsPage: React.FC = () => {
  const { t } = useLanguage();
  const [, setTick] = useState(0);

  // View state: 'list' (default) or 'form' (new/edit)
  const [view, setView] = useState<'list' | 'form'>('list');

  // Grid vs Table toggle for list view (initialized from user default preference)
  const [viewMode, setViewMode] = useDefaultViewMode();

  // Form Data state
  const [formData, setFormData] = useState<Partial<PlanningControl>>(emptyControl());
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    return academyStore.subscribe(() => setTick(t => t + 1));
  }, []);

  const controls = academyStore.getPlanningControls();

  // ── Navigation Helpers ─────────────────────────────────────────────────────

  const openNew = () => {
    setFormData(emptyControl());
    setView('form');
  };

  const openEdit = (ctrl: PlanningControl) => {
    setFormData({ ...ctrl });
    setView('form');
  };

  const backToList = () => {
    setView('list');
    setFormData(emptyControl());
  };

  const handleValidate = (): string | null => {
    if (!formData.code?.trim()) return t('academy.planning_controls.validation.code_required');
    if (!formData.lib?.trim()) return t('academy.planning_controls.validation.lib_required');
    return null;
  };

  // GenericPage auto-updates IndexedDB → academyStore refreshes via subscription.
  const handleDataChange = () => {
    setSuccessMsg(t('academy.planning_controls.msg_saved'));
    setTimeout(() => setSuccessMsg(null), 3000);
    backToList();
  };


  const handleClear = () => {
    setFormData(emptyControl());
  };

  const handleDeleteControl = async (ctrl: PlanningControl) => {
    // boubaker 23/08/2026 - confirmation via le composant générique animé
    const confirmed = await blobticMessage.confirm({
      title: t('common.confirm.title.delete'),
      message: t('academy.planning_controls.confirm_delete'),
      severity: 'warning',
      confirmText: t('common.actions.confirm'),
      cancelText: t('btn.cancel')
    });
    if (!confirmed) return;
    const res = await academyStore.deletePlanningControl(ctrl.id);
    if (res.success) {
      blobticMessage.success(t('common.messages.success'), t('academy.planning_controls.msg_deleted', ctrl.code));
    } else {
      blobticMessage.error(t('common.error'), res.message || 'Erreur lors de la suppression.');
    }
  };

  const activeCount = controls.filter(c => c.is_active).length;
  const blockingCount = controls.filter(c => c.is_active && c.is_blocking).length;
  const warningCount = controls.filter(c => c.is_active && !c.is_blocking).length;

  // ─── LIST VIEW ─────────────────────────────────────────────────────────────

  if (view === 'list') {
    return (
      <GenericPage
        title={t('academy.planning_controls.title')}
        icon={<ControlsIcon />}
        footerEnabled={false}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onNew={openNew}
        newButtonLabel={t('academy.planning_controls.add_new')}
      >
        <Box sx={{ pb: 4 }}>
          {/* Success Notification Banner */}
          {successMsg && (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3, borderRadius: 2 }}>
              {successMsg}
            </Alert>
          )}

          {/* Overview Stats Cards */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined" sx={{ borderRadius: 3, borderLeft: '6px solid #6366f1' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('academy.planning_controls.active_count')}
                  </Typography>
                  <Typography variant="h4" color="primary.main" fontWeight="bold">
                    {activeCount} / {controls.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card variant="outlined" sx={{ borderRadius: 3, borderLeft: '6px solid #ef4444' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('academy.planning_controls.blocking_count')}
                  </Typography>
                  <Typography variant="h4" color="error.main" fontWeight="bold">
                    {blockingCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card variant="outlined" sx={{ borderRadius: 3, borderLeft: '6px solid #f59e0b' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('academy.planning_controls.warning_count')}
                  </Typography>
                  <Typography variant="h4" color="warning.main" fontWeight="bold">
                    {warningCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* ── VIEW MODE RENDERING (TABLE vs GRID) ── */}
          {viewMode === 'table' ? (
            /* TABLE VIEW USING GenericTable (Read-only switches in list view) */
            <GenericTable
              columns={[
                {
                  id: 'code',
                  label: t('academy.planning_controls.code'),
                  minWidth: 150,
                  format: (val: any) => (
                    <Chip label={val} size="small" color="primary" variant="outlined" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }} />
                  )
                },
                {
                  id: 'lib',
                  label: t('academy.planning_controls.lib'),
                  minWidth: 200,
                  format: (val: any) => (
                    <Typography variant="body2" fontWeight="bold">{val}</Typography>
                  )
                },
                {
                  id: 'description',
                  label: t('academy.planning_controls.description'),
                  minWidth: 250,
                  format: (val: any) => (
                    <Typography variant="body2" color="text.secondary">{val || '—'}</Typography>
                  )
                },
                {
                  id: 'is_active',
                  label: t('academy.planning_controls.active'),
                  minWidth: 100,
                  format: (_: any, row: PlanningControl) => (
                    <Switch
                      checked={row.is_active}
                      disabled
                      color="primary"
                      size="small"
                    />
                  )
                },
                {
                  id: 'is_blocking',
                  label: t('academy.planning_controls.blocking'),
                  minWidth: 130,
                  format: (_: any, row: PlanningControl) => (
                    <Switch
                      checked={row.is_blocking}
                      disabled
                      color="error"
                      size="small"
                    />
                  )
                },
                {
                  id: 'status',
                  label: t('academy.planning_controls.status'),
                  minWidth: 140,
                  format: (_: any, row: PlanningControl) => (
                    !row.is_active ? (
                      <Chip label={t('academy.planning_controls.status_disabled')} size="small" variant="outlined" color="default" />
                    ) : row.is_blocking ? (
                      <Chip label={t('academy.planning_controls.status_blocking')} size="small" color="error" icon={<LockIcon />} />
                    ) : (
                      <Chip label={t('academy.planning_controls.status_warning')} size="small" color="warning" icon={<WarningIcon />} />
                    )
                  )
                }
              ]}
              data={controls}
              onEdit={(row: PlanningControl) => openEdit(row)}
              onDelete={(row: PlanningControl) => handleDeleteControl(row)}
            />
          ) : (
            /* GRID VIEW (Read-only switches in list view) */
            <Grid container spacing={2.5}>
              {controls.map(ctrl => (
                <Grid item xs={12} sm={6} md={4} key={ctrl.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      borderColor: !ctrl.is_active ? 'divider' : ctrl.is_blocking ? 'error.light' : 'warning.light',
                      opacity: ctrl.is_active ? 1 : 0.7,
                      transition: 'transform 0.2s',
                      '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Chip label={ctrl.code} size="small" color="primary" variant="outlined" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }} />
                        {!ctrl.is_active ? (
                          <Chip label={t('academy.planning_controls.status_disabled')} size="small" variant="outlined" color="default" />
                        ) : ctrl.is_blocking ? (
                          <Chip label={t('academy.planning_controls.status_blocking')} size="small" color="error" icon={<LockIcon />} />
                        ) : (
                          <Chip label={t('academy.planning_controls.status_warning')} size="small" color="warning" icon={<WarningIcon />} />
                        )}
                      </Box>

                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                        {ctrl.lib}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40, mb: 2 }}>
                        {ctrl.description || t('academy.planning_controls.no_description')}
                      </Typography>

                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2, bgcolor: 'action.hover' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="caption" fontWeight="bold">
                            {t('academy.planning_controls.active')} :
                          </Typography>
                          <Switch
                            checked={ctrl.is_active}
                            disabled
                            color="primary"
                            size="small"
                          />
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" fontWeight="bold">
                            {t('academy.planning_controls.blocking')} :
                          </Typography>
                          <Switch
                            checked={ctrl.is_blocking}
                            disabled
                            color="error"
                            size="small"
                          />
                        </Stack>
                      </Paper>

                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(ctrl)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteControl(ctrl)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </GenericPage>
    );
  }

  // ─── FORM VIEW (Standard CRUD Page with Top Action Bar & Footer Audit) ────────────

  const isEditing = Boolean(formData.id);
  const formPayload = controlToDto(formData);

  return (
    <GenericPage
      title={isEditing ? t('academy.planning_controls.modal_title_edit') : t('academy.planning_controls.modal_title_add')}
      icon={<ControlsIcon />}
      footerEnabled={true}
      footerInfo={{
        lastUpdate: formData.last_updt || '-',
        userLib: formData.user_lib || (formData as any).USER_LIB || '-',
      }}
      additionalActions={
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={backToList}
          sx={{ borderRadius: 2 }}
        >
          {t('academy.planning_controls.back_to_list')}
        </Button>
      }
      crudConfig={{
        entityKey: ENTITY_KEY,
        actionSuffix: ACTION_SUFFIX,
        primaryKey: PRIMARY_KEY,
      }}
      data={formPayload}
      onDataChange={() => handleDataChange()}

      onValidate={handleValidate}
      onClear={handleClear}
    >
      <Paper elevation={3} sx={{ maxWidth: 800, mx: 'auto', p: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label={t('academy.planning_controls.code')}
            value={formData.code || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
            fullWidth
            required
            disabled={isEditing}
            helperText={t('academy.planning_controls.code_helper')}
          />

          <TextField
            label={t('academy.planning_controls.lib')}
            value={formData.lib || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, lib: e.target.value }))}
            fullWidth
            required
            helperText={t('academy.planning_controls.lib_helper')}
          />

          <TextField
            label={t('academy.planning_controls.description')}
            value={formData.description || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            fullWidth
            multiline
            rows={3}
            helperText={t('academy.planning_controls.desc_helper')}
          />

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('academy.planning_controls.active')} (is_active)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('academy.planning_controls.active_desc')}
                </Typography>
              </Box>
              <Switch
                checked={Boolean(formData.is_active)}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                color="primary"
              />
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" color="error.main">
                  {t('academy.planning_controls.blocking')} (is_blocking)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('academy.planning_controls.blocking_desc')}
                </Typography>
              </Box>
              <Switch
                checked={Boolean(formData.is_blocking)}
                disabled={!formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_blocking: e.target.checked }))}
                color="error"
              />
            </Stack>
          </Paper>
        </Box>
      </Paper>
    </GenericPage>
  );
};

export default PlanningControlsPage;
